/**
 * Cellar state shape and the two invariant checks shared by every change
 * function: wine-identity uniqueness (FR-002/FR-003) and stock never going
 * negative (FR-014). Concrete change functions (recordPurchase, drinkBottle,
 * undoDrink, rateTasting, addOfflineTasting, edit/delete helpers) are added
 * by their own user-story tasks (T030, T043, T113...), extending this file.
 *
 * See specs/001-wine-cellar-tracker/data-model.md for the full field list
 * and specs/001-wine-cellar-tracker/plan.md "Change handling" for how this
 * fits into the optimistic-update store (app/src/store/cellar-store.ts).
 */
import { sameWineIdentity, wineIdentityKey, type WineIdentityFields } from './identity';
import { isPhotoId, type LabelPhotos, type LabelSide } from './label-photos';
import { computeStock, ensureStockNotNegative } from './stock';
import {
  currentMonth,
  isFutureDay,
  isFutureMonth,
  isRealDay,
  todayInParis,
  type CalendarDay,
  type MonthValue,
} from './months';
import { isValidRating } from './rating';
import { COUNTRIES } from './countries';
import { CELLAR_SCHEMA_VERSION } from './cellar-schema';
import { isRegionOf } from './regions';
import { checkedOnlineRating, type SavedOnlineRating } from './online-rating';

export type TypeDeVin = 'rouge' | 'blanc' | 'rose' | 'vendanges_tardives';

/** Display labels (FR-035): Rouge, Blanc, Rosé, Vendanges tardives. */
export const TYPE_DE_VIN_LABELS: Record<TypeDeVin, string> = {
  rouge: 'Rouge',
  blanc: 'Blanc',
  rose: 'Rosé',
  vendanges_tardives: 'Vendanges tardives',
};

export interface Purchase {
  id: string;
  quantity: number;
  unitPriceCents: number;
  purchasedOn: MonthValue;
  /** The day of the month it was bought (schema version 6, owner 2026-10-06); older ones lack it. */
  purchasedDay?: number;
  /** Free-text details of the purchase (schema version 6). */
  notes?: string;
  createdAt: number;
}

export interface Tasting {
  id: string;
  tastedOn: MonthValue;
  /** Increasing per-wine counter giving entry order; see domain/rating.ts. */
  sequence: number;
  rating?: number;
  notes?: string;
  consumedFromStock: boolean;
  /**
   * Bottles taken from stock by this tasting, when more than one (schema version 7, owner
   * 2026-10-09: "Boire et noter" several bottles at once). Absent means one; see stock.ts.
   */
  bottles?: number;
  createdAt: number;
}

export interface Wine extends WineIdentityFields {
  id: string;
  pays: string;
  /**
   * One of the country's regions, or "other" (domain/regions.ts; schema version 3, 2026-10-03).
   * Required for new wines and edits; wines saved before may not have one yet.
   */
  region?: string;
  typeDeVin: TypeDeVin;
  /** The last year to drink it, due at its end (a year only since schema version 6). */
  drinkUntilYear?: number;
  drinkFromYear?: number;
  /**
   * The last "Note en ligne" lookup (schema version 4, 2026-10-03), kept apart from the tastings;
   * see domain/online-rating.ts.
   */
  onlineRating?: SavedOnlineRating;
  /** The label photos' names (schema version 8, owner 2026-10-11; domain/label-photos.ts). */
  labelPhotos?: LabelPhotos;
  purchases: Purchase[];
  tastings: Tasting[];
  /** Incremented on every change to the wine or its purchases/tastings. */
  version: number;
  createdAt: number;
  updatedAt: number;
}

export interface Cellar {
  schemaVersion: number;
  wines: Wine[];
}

/**
 * A pure `Cellar -> Cellar` change. Every user action (recordPurchase,
 * drinkBottle...) is one of these: it must throw for an invalid change
 * rather than silently doing nothing, so app/src/store/cellar-store.ts can
 * apply it optimistically and safely replay it against fresher data on a
 * conflict (plan.md "Change handling").
 */
export type CellarChange = (cellar: Cellar) => Cellar;

export function createEmptyCellar(): Cellar {
  return { schemaVersion: CELLAR_SCHEMA_VERSION, wines: [] };
}

/**
 * Deletes everything in a cellar (FR-039, T074): every wine with its purchases and tastings.
 * The document and its person remain, so the result is an empty cellar.
 */
export const emptyCellar: CellarChange = (cellar) => ({ ...cellar, wines: [] });

/** A reasonably unique id for a new wine/purchase/tasting. */
function createId(): string {
  return crypto.randomUUID();
}

/** Finds the wine in `cellar` matching `identity`, per the normalised identity rule. */
export function findWineByIdentity(cellar: Cellar, identity: WineIdentityFields): Wine | undefined {
  return cellar.wines.find((wine) => sameWineIdentity(wine, identity));
}

/** Thrown when a wine's identity fields would duplicate another wine already in the cellar. */
export class WineIdentityConflictError extends Error {
  constructor(
    public readonly conflictingWineId: string,
    message = 'Ce vin existe déjà dans votre cave.',
  ) {
    super(message);
    this.name = 'WineIdentityConflictError';
  }
}

/**
 * Throws WineIdentityConflictError if `identity` matches an existing wine
 * other than `excludeWineId` (pass the wine's own id when editing it).
 */
export function assertNoIdentityConflict(
  cellar: Cellar,
  identity: WineIdentityFields,
  excludeWineId?: string,
): void {
  const conflict = cellar.wines.find(
    (wine) => wine.id !== excludeWineId && sameWineIdentity(wine, identity),
  );
  if (conflict) {
    throw new WineIdentityConflictError(conflict.id);
  }
}

/** A wine's current stock: total bottles purchased minus total bottles drunk. */
export function wineStock(wine: Pick<Wine, 'purchases' | 'tastings'>): number {
  return computeStock(wine);
}

/** Throws StockError (domain/stock.ts) if `wine`'s stock is negative. */
export function assertWineStockNotNegative(wine: Pick<Wine, 'purchases' | 'tastings'>): void {
  ensureStockNotNegative(wineStock(wine));
}

// Re-exported so callers of domain/cellar.ts don't also need domain/identity.ts
// for the common case of comparing two identities.
export { wineIdentityKey };

// --- User Story 1: record a purchase (spec.md FR-007-FR-010) ---------------

/** Thrown for a purchase or tasting dated later than the current month. */
export class FutureMonthError extends Error {
  constructor(message = 'Le mois choisi est dans le futur.') {
    super(message);
    this.name = 'FutureMonthError';
  }
}

/** Thrown for a structurally invalid purchase (bad quantity or price). */
export class InvalidPurchaseError extends Error {
  constructor(
    message: string,
    /** Which field is wrong, so the screen can show the message in the person's language. */
    public readonly field: 'quantity' | 'price' | 'date' = 'quantity',
  ) {
    super(message);
    this.name = 'InvalidPurchaseError';
  }
}

export interface WineDetails extends WineIdentityFields {
  pays: string;
  region?: string;
  typeDeVin: TypeDeVin;
  drinkUntilYear?: number;
  drinkFromYear?: number;
}

export interface RecordPurchaseInput {
  /**
   * The wine's full identity and other fields. If this matches an existing
   * wine (by normalised identity, FR-003) the purchase is added to it and
   * the rest of these fields are ignored, exactly as if the caller had
   * picked that existing wine — a duplicate can never be created this way.
   */
  wine: WineDetails;
  quantity: number;
  unitPriceCents: number;
  purchasedOn: MonthValue;
  /** The day of the month, when known (schema version 6). */
  purchasedDay?: number;
  notes?: string;
  /** A "Note en ligne" lookup made on the purchase form, saved on the wine (new or existing). */
  onlineRating?: SavedOnlineRating;
  /**
   * A wine tasted elsewhere (owner, 2026-10-11): with a quantity of 0, the rating and comment given
   * on the purchase form, kept as a tasting outside the cellar this month (stock unchanged).
   */
  tastedElsewhere?: { rating?: number; notes?: string };
  /** The scanned label photos (owner, 2026-10-11): set on the wine, replacing those sides. */
  labelPhotos?: LabelPhotos;
}

/**
 * Logs a purchase (FR-007-FR-010): adds to the matching existing wine, or
 * creates a new one. `now` defaults to the real current month and is only
 * ever overridden by tests. Since 2026-10-11 the quantity may be 0 (a wine
 * tasted elsewhere), with its rating and comment as an out-of-stock tasting.
 */
export function recordPurchase(
  input: RecordPurchaseInput,
  now: MonthValue = currentMonth(),
  today: CalendarDay = todayInParis(),
): CellarChange {
  const { tastedElsewhere } = input;
  if (tastedElsewhere === undefined) return recordPurchaseOnly(input, now, today);
  return (cellar) => {
    if (input.quantity !== 0) {
      throw new InvalidPurchaseError(
        'Une note à l’achat n’est possible que pour un vin dégusté ailleurs (quantité 0).',
      );
    }
    const next = recordPurchaseOnly(input, now, today)(cellar);
    const wine = findWineByIdentity(next, input.wine)!;
    const notes = tastedElsewhere.notes?.trim();
    return addOfflineTasting(
      {
        wineId: wine.id,
        ...(tastedElsewhere.rating !== undefined ? { rating: tastedElsewhere.rating } : {}),
        ...(notes ? { notes } : {}),
      },
      now,
    )(next);
  };
}

function recordPurchaseOnly(
  input: RecordPurchaseInput,
  now: MonthValue,
  today: CalendarDay,
): CellarChange {
  return (cellar) => {
    if (!Number.isInteger(input.quantity) || input.quantity < 0) {
      throw new InvalidPurchaseError('La quantité doit être un nombre entier positif ou nul.');
    }
    if (!isWholeEuros(input.unitPriceCents)) {
      throw new InvalidPurchaseError(
        'Le prix unitaire doit être un nombre entier d’euros, positif ou nul.',
        'price',
      );
    }
    if (isFutureMonth(input.purchasedOn, now)) {
      throw new FutureMonthError();
    }
    assertPurchaseDay(input.purchasedOn, input.purchasedDay, today);
    const notes = input.notes?.trim() || undefined;
    const onlineRating =
      input.onlineRating !== undefined ? checkedOnlineRating(input.onlineRating, now) : undefined;
    const withOnlineRating = onlineRating !== undefined ? { onlineRating } : {};
    const scanned = checkedLabelPhotos(input.labelPhotos);

    const timestamp = Date.now();
    const purchase: Purchase = {
      id: createId(),
      quantity: input.quantity,
      unitPriceCents: input.unitPriceCents,
      purchasedOn: input.purchasedOn,
      ...(input.purchasedDay !== undefined ? { purchasedDay: input.purchasedDay } : {}),
      ...(notes !== undefined ? { notes } : {}),
      createdAt: timestamp,
    };

    const existing = findWineByIdentity(cellar, input.wine);
    if (existing) {
      const updated: Wine = {
        ...existing,
        ...withOnlineRating,
        ...(scanned ? { labelPhotos: { ...existing.labelPhotos, ...scanned } } : {}),
        purchases: [...existing.purchases, purchase],
        version: existing.version + 1,
        updatedAt: timestamp,
      };
      return { ...cellar, wines: cellar.wines.map((w) => (w.id === existing.id ? updated : w)) };
    }

    if (input.wine.region === undefined || !isRegionOf(input.wine.pays, input.wine.region)) {
      throw new InvalidWineError('region', 'Choisissez une région du pays.');
    }
    const newWine: Wine = {
      id: createId(),
      ...input.wine,
      ...withOnlineRating,
      ...(scanned ? { labelPhotos: scanned } : {}),
      purchases: [purchase],
      tastings: [],
      version: 1,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    return { ...cellar, wines: [...cellar.wines, newWine] };
  };
}

/** The scanned photos with only their safe names, or nothing when none was given. */
function checkedLabelPhotos(photos: LabelPhotos | undefined): LabelPhotos | undefined {
  if (photos === undefined) return undefined;
  const sides = (['front', 'back'] as const).filter((side) => photos[side] !== undefined);
  if (sides.some((side) => !isPhotoId(photos[side]!))) {
    throw new InvalidPurchaseError('Photo de l’étiquette invalide.');
  }
  return sides.length > 0 ? Object.fromEntries(sides.map((s) => [s, photos[s]])) : undefined;
}

export interface SetLabelPhotoInput {
  wineId: string;
  side: LabelSide;
  photoId: string;
}

/**
 * Takes or replaces one label photo on a wine's page (owner request, 2026-10-11). The photo is
 * stored first; this only names it on the wine.
 */
export function setLabelPhoto(input: SetLabelPhotoInput): CellarChange {
  return (cellar) => {
    if (!isPhotoId(input.photoId)) throw new InvalidPurchaseError('Photo de l’étiquette invalide.');
    const wine = findWineOrThrow(cellar, input.wineId);
    return replaceWine(
      cellar,
      touched(wine, { labelPhotos: { ...wine.labelPhotos, [input.side]: input.photoId } }),
    );
  };
}

export interface RemoveLabelPhotoInput {
  wineId: string;
  side: LabelSide;
}

/**
 * Removes one label photo from a wine (owner request, 2026-10-15: the "×" in its corner, to take
 * it again). Only the wine's name for it goes; the stored JPEG stays in the versioned bucket.
 * Nothing to remove leaves the cellar as it is.
 */
export function removeLabelPhoto(input: RemoveLabelPhotoInput): CellarChange {
  return (cellar) => {
    const wine = findWineOrThrow(cellar, input.wineId);
    if (wine.labelPhotos?.[input.side] === undefined) return cellar;
    const { [input.side]: _removed, ...kept } = wine.labelPhotos;
    const { labelPhotos: _old, ...rest } = wine;
    const next: Wine = Object.keys(kept).length > 0 ? { ...rest, labelPhotos: kept } : rest;
    return replaceWine(cellar, touched(next, {}));
  };
}

/** A price in cents that is a whole number of euros, zero or more (owner, 2026-10-15). */
function isWholeEuros(cents: number): boolean {
  return Number.isInteger(cents) && cents >= 0 && cents % 100 === 0;
}

// --- User Story 2: drink a bottle and rate it (spec.md FR-011-FR-017) ------

/** Thrown when a change refers to a wine id that isn't in the cellar. */
export class WineNotFoundError extends Error {
  constructor(message = "Ce vin n'existe plus dans votre cave.") {
    super(message);
    this.name = 'WineNotFoundError';
  }
}

/** Thrown for a rating outside 1-5 or not on a half-point step. */
export class InvalidTastingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidTastingError';
  }
}

function findWineOrThrow(cellar: Cellar, wineId: string): Wine {
  const wine = cellar.wines.find((w) => w.id === wineId);
  if (!wine) {
    throw new WineNotFoundError();
  }
  return wine;
}

function replaceWine(cellar: Cellar, updated: Wine): Cellar {
  return { ...cellar, wines: cellar.wines.map((w) => (w.id === updated.id ? updated : w)) };
}

export interface DrinkBottleInput {
  wineId: string;
  tastedOn?: MonthValue;
  /**
   * Optional explicit id for the created tasting. The UI generates and
   * passes one so it can reference this exact tasting for "Annuler" or a
   * follow-up rating before the change is confirmed saved.
   */
  tastingId?: string;
  /** Bottles drunk, a whole number from 1 (owner 2026-10-09); one when left out. */
  bottles?: number;
  /** Optional rating from the drink form (FR-012 as changed by the owner on 2026-09-28). */
  rating?: number;
  /** Optional tasting notes from the drink form; blank notes are not stored. */
  notes?: string;
}

/**
 * "Boire une bouteille" (FR-012): reduces stock by one, or by the bottles chosen in "Boire et
 * noter" (owner 2026-10-09), and creates one tasting for the current month, with the optional
 * rating and notes the person gave in the drink form, all in one change. The card only applies
 * it once the person confirms (owner decision, 2026-09-28).
 */
export function drinkBottle(
  input: DrinkBottleInput,
  now: MonthValue = currentMonth(),
): CellarChange {
  return (cellar) => {
    const wine = findWineOrThrow(cellar, input.wineId);
    const tastedOn = input.tastedOn ?? now;
    if (isFutureMonth(tastedOn, now)) {
      throw new FutureMonthError();
    }
    if (input.rating !== undefined && !isValidRating(input.rating)) {
      throw new InvalidTastingError('La note doit être comprise entre 1 et 5, par demi-point.');
    }
    const bottles = input.bottles ?? 1;
    if (!Number.isInteger(bottles) || bottles < 1) {
      throw new InvalidTastingError(
        'Le nombre de bouteilles doit être un nombre entier d’au moins 1.',
      );
    }
    const notes = input.notes?.trim();
    const timestamp = Date.now();
    const tasting: Tasting = {
      id: input.tastingId ?? createId(),
      tastedOn,
      sequence: wine.tastings.length,
      ...(bottles > 1 ? { bottles } : {}),
      ...(input.rating !== undefined ? { rating: input.rating } : {}),
      ...(notes ? { notes } : {}),
      consumedFromStock: true,
      createdAt: timestamp,
    };
    const updated: Wine = {
      ...wine,
      tastings: [...wine.tastings, tasting],
      version: wine.version + 1,
      updatedAt: timestamp,
    };
    assertWineStockNotNegative(updated);
    return replaceWine(cellar, updated);
  };
}

export interface UndoDrinkInput {
  wineId: string;
  tastingId: string;
}

/**
 * The exact inverse of drinkBottle for the same tasting: removes it,
 * restoring stock. Used for "Annuler" right after drinking (FR-012); undo
 * is otherwise nothing special, just applying the inverse change
 * (plan.md "Change handling").
 */
export function undoDrink(input: UndoDrinkInput): CellarChange {
  return (cellar) => {
    const wine = findWineOrThrow(cellar, input.wineId);
    if (!wine.tastings.some((t) => t.id === input.tastingId)) {
      throw new Error('Cette dégustation a déjà été annulée ou modifiée.');
    }
    const updated: Wine = {
      ...wine,
      tastings: wine.tastings.filter((t) => t.id !== input.tastingId),
      version: wine.version + 1,
      updatedAt: Date.now(),
    };
    return replaceWine(cellar, updated);
  };
}

export interface RateTastingInput {
  wineId: string;
  tastingId: string;
  rating: number;
  notes?: string;
}

/**
 * Adds or changes a tasting's rating and notes (FR-013) — used right after
 * drinking, from the wine's page, or on an older unrated tasting.
 */
export function rateTasting(input: RateTastingInput): CellarChange {
  return (cellar) => {
    if (!isValidRating(input.rating)) {
      throw new InvalidTastingError('La note doit être comprise entre 1 et 5, par demi-point.');
    }
    const wine = findWineOrThrow(cellar, input.wineId);
    const tasting = wine.tastings.find((t) => t.id === input.tastingId);
    if (!tasting) {
      throw new Error("Cette dégustation n'existe plus.");
    }
    const updatedTasting: Tasting = {
      ...tasting,
      rating: input.rating,
      ...(input.notes !== undefined ? { notes: input.notes } : {}),
    };
    const updated: Wine = {
      ...wine,
      tastings: wine.tastings.map((t) => (t.id === tasting.id ? updatedTasting : t)),
      version: wine.version + 1,
      updatedAt: Date.now(),
    };
    return replaceWine(cellar, updated);
  };
}

export interface AddOfflineTastingInput {
  wineId: string;
  rating?: number;
  notes?: string;
  tastedOn?: MonthValue;
}

/**
 * A tasting of a bottle not from cellar stock (FR-015). Never touches stock, even at 0. Since
 * 2026-10-11 its only path is a purchase of 0 bottles with a rating (`recordPurchase`); the
 * "Ajouter une dégustation" form is gone.
 */
export function addOfflineTasting(
  input: AddOfflineTastingInput,
  now: MonthValue = currentMonth(),
): CellarChange {
  return (cellar) => {
    if (input.rating !== undefined && !isValidRating(input.rating)) {
      throw new InvalidTastingError('La note doit être comprise entre 1 et 5, par demi-point.');
    }
    const wine = findWineOrThrow(cellar, input.wineId);
    const tastedOn = input.tastedOn ?? now;
    if (isFutureMonth(tastedOn, now)) {
      throw new FutureMonthError();
    }
    const timestamp = Date.now();
    const tasting: Tasting = {
      id: createId(),
      tastedOn,
      sequence: wine.tastings.length,
      consumedFromStock: false,
      createdAt: timestamp,
      ...(input.rating !== undefined ? { rating: input.rating } : {}),
      ...(input.notes !== undefined ? { notes: input.notes } : {}),
    };
    const updated: Wine = {
      ...wine,
      tastings: [...wine.tastings, tasting],
      version: wine.version + 1,
      updatedAt: timestamp,
    };
    return replaceWine(cellar, updated);
  };
}

// --- User Story 7: edit and delete in place (T113, FR-029, spec.md Story 7) ------------------

/** Thrown for a wine field that breaks its rule (data-model.md "Wine"). */
export class InvalidWineError extends Error {
  constructor(
    public readonly field:
      'domaine' | 'cepage' | 'annee' | 'pays' | 'region' | 'typeDeVin' | 'drinkUntil',
    message = 'Cette valeur n’est pas valable.',
  ) {
    super(message);
    this.name = 'InvalidWineError';
  }
}

/**
 * Thrown when the record changed since the person opened it (another device saved first), or no
 * longer exists: nothing is overwritten (contracts/ui-and-emails.md, "modifiée entre-temps").
 */
export class RecordChangedError extends Error {
  constructor(
    message = 'Cette fiche a été modifiée entre-temps. Rechargez-la avant de continuer.',
  ) {
    super(message);
    this.name = 'RecordChangedError';
  }
}

/** Thrown for a month that is not a real month (e.g. a year typed with letters). */
export class InvalidMonthError extends Error {
  constructor(message = 'Le mois n’est pas valable.') {
    super(message);
    this.name = 'InvalidMonthError';
  }
}

function assertRealMonth(value: MonthValue): void {
  if (
    !Number.isInteger(value.year) ||
    value.year < 1800 ||
    !Number.isInteger(value.month) ||
    value.month < 1 ||
    value.month > 12
  ) {
    throw new InvalidMonthError();
  }
}

const WINE_TYPES: readonly TypeDeVin[] = ['rouge', 'blanc', 'rose', 'vendanges_tardives'];

/** A purchase's day (schema version 6): a real day of its month, not later than today. */
function assertPurchaseDay(month: MonthValue, day: number | undefined, today: CalendarDay): void {
  if (day === undefined) return;
  const date = { year: month.year, month: month.month, day };
  if (!isRealDay(date)) throw new InvalidPurchaseError('Cette date n’existe pas.', 'date');
  if (isFutureDay(date, today)) throw new FutureMonthError();
}

/** Trimmed, inner whitespace collapsed; undefined when nothing is left. */
function tidy(value: string | undefined): string | undefined {
  const text = value?.trim().replace(/\s+/g, ' ');
  return text ? text : undefined;
}

/** The wine at the version the person saw, or RecordChangedError / WineNotFoundError. */
function wineAtVersion(cellar: Cellar, wineId: string, expectedVersion: number): Wine {
  const wine = findWineOrThrow(cellar, wineId);
  if (wine.version !== expectedVersion) throw new RecordChangedError();
  return wine;
}

function touched(wine: Wine, changes: Partial<Wine>): Wine {
  return { ...wine, ...changes, version: wine.version + 1, updatedAt: Date.now() };
}

export interface SetOnlineRatingInput {
  wineId: string;
  onlineRating: SavedOnlineRating;
}

/**
 * Saves a "Note en ligne" lookup on a wine (owner request, 2026-10-03), replacing the previous
 * one. It is not a tasting, so it never touches the wine's ratings or stock.
 */
export function setOnlineRating(
  input: SetOnlineRatingInput,
  now: MonthValue = currentMonth(),
): CellarChange {
  return (cellar) => {
    const wine = findWineOrThrow(cellar, input.wineId);
    return replaceWine(
      cellar,
      touched(wine, { onlineRating: checkedOnlineRating(input.onlineRating, now) }),
    );
  };
}

export interface EditWineInput {
  wineId: string;
  /** The wine's `version` when the person started editing. */
  expectedVersion: number;
  /** Every field of the wine as it should now be; a field left out (or blank) is cleared. */
  details: WineDetails;
}

/**
 * Edits a wine's identity and details (FR-029) with the creation rules: required fields, a
 * plausible vintage (1800 to next year), a known country, a drink-by month only with a year, and
 * no other wine with the same identity (FR-003) — the error names that wine.
 */
export function editWine(input: EditWineInput, now: MonthValue = currentMonth()): CellarChange {
  return (cellar) => {
    const wine = wineAtVersion(cellar, input.wineId, input.expectedVersion);
    const { details } = input;
    const domaine = tidy(details.domaine);
    const cepage = tidy(details.cepage);
    const parcelle = tidy(details.parcelle);
    if (!domaine) throw new InvalidWineError('domaine', 'Le Domaine est obligatoire.');
    if (!cepage) throw new InvalidWineError('cepage', 'Le Cépage est obligatoire.');
    if (!Number.isInteger(details.annee) || details.annee < 1800 || details.annee > now.year + 1) {
      throw new InvalidWineError('annee', 'L’année doit être un millésime plausible.');
    }
    if (!COUNTRIES.some((country) => country.code === details.pays)) {
      throw new InvalidWineError('pays', 'Choisissez un pays de la liste.');
    }
    if (details.region === undefined || !isRegionOf(details.pays, details.region)) {
      throw new InvalidWineError('region', 'Choisissez une région du pays.');
    }
    if (!WINE_TYPES.includes(details.typeDeVin)) {
      throw new InvalidWineError('typeDeVin', 'Choisissez un type de vin.');
    }
    const { drinkUntilYear } = details;
    if (drinkUntilYear !== undefined && !Number.isInteger(drinkUntilYear)) {
      throw new InvalidWineError('drinkUntil', 'L’échéance de dégustation n’est pas valable.');
    }
    const identity = { domaine, cepage, ...(parcelle ? { parcelle } : {}), annee: details.annee };
    assertNoIdentityConflict(cellar, identity, wine.id);

    const {
      parcelle: _parcelle,
      region: _region,
      drinkUntilYear: _year,
      drinkFromYear: _from,
      ...kept
    } = wine;
    const updated: Wine = touched(kept, {
      ...identity,
      pays: details.pays,
      ...(details.region !== undefined ? { region: details.region } : {}),
      typeDeVin: details.typeDeVin,
      ...(drinkUntilYear !== undefined ? { drinkUntilYear } : {}),
      ...(details.drinkFromYear !== undefined ? { drinkFromYear: details.drinkFromYear } : {}),
    });
    return replaceWine(cellar, updated);
  };
}

export interface EditPurchaseInput {
  wineId: string;
  purchaseId: string;
  expectedVersion: number;
  quantity: number;
  unitPriceCents: number;
  purchasedOn: MonthValue;
  /** Left out when the day is not known (schema version 6). */
  purchasedDay?: number;
  /** Left out or blank to remove the details. */
  notes?: string;
}

/** Edits a purchase (FR-029) with the creation rules; the stock may not go below zero (FR-014). */
export function editPurchase(
  input: EditPurchaseInput,
  now: MonthValue = currentMonth(),
  today: CalendarDay = todayInParis(),
): CellarChange {
  return (cellar) => {
    const wine = wineAtVersion(cellar, input.wineId, input.expectedVersion);
    const purchase = wine.purchases.find((p) => p.id === input.purchaseId);
    if (!purchase) throw new RecordChangedError();
    if (!Number.isInteger(input.quantity) || input.quantity < 0) {
      throw new InvalidPurchaseError('La quantité doit être un nombre entier positif ou nul.');
    }
    if (!isWholeEuros(input.unitPriceCents)) {
      throw new InvalidPurchaseError(
        'Le prix unitaire doit être un nombre entier d’euros, positif ou nul.',
        'price',
      );
    }
    assertRealMonth(input.purchasedOn);
    if (isFutureMonth(input.purchasedOn, now)) throw new FutureMonthError();
    assertPurchaseDay(input.purchasedOn, input.purchasedDay, today);
    const { purchasedDay: _day, notes: _notes, ...kept } = purchase;
    const notes = input.notes?.trim() || undefined;
    const edited: Purchase = {
      ...kept,
      quantity: input.quantity,
      unitPriceCents: input.unitPriceCents,
      purchasedOn: input.purchasedOn,
      ...(input.purchasedDay !== undefined ? { purchasedDay: input.purchasedDay } : {}),
      ...(notes ? { notes } : {}),
    };
    const updated = touched(wine, {
      purchases: wine.purchases.map((p) => (p.id === purchase.id ? edited : p)),
    });
    assertWineStockNotNegative(updated);
    return replaceWine(cellar, updated);
  };
}

export interface EditTastingInput {
  wineId: string;
  tastingId: string;
  expectedVersion: number;
  tastedOn: MonthValue;
  /** Left out to remove the rating. */
  rating?: number;
  /** Left out or blank to remove the notes. */
  notes?: string;
}

/**
 * Edits a tasting's month, rating and notes (FR-029). Whether it took a bottle from the stock is
 * not editable, so the stock never changes here.
 */
export function editTasting(
  input: EditTastingInput,
  now: MonthValue = currentMonth(),
): CellarChange {
  return (cellar) => {
    const wine = wineAtVersion(cellar, input.wineId, input.expectedVersion);
    const tasting = wine.tastings.find((t) => t.id === input.tastingId);
    if (!tasting) throw new RecordChangedError();
    if (input.rating !== undefined && !isValidRating(input.rating)) {
      throw new InvalidTastingError('La note doit être comprise entre 1 et 5, par demi-point.');
    }
    assertRealMonth(input.tastedOn);
    if (isFutureMonth(input.tastedOn, now)) throw new FutureMonthError();
    const { rating: _rating, notes: _notes, ...kept } = tasting;
    const notes = input.notes?.trim();
    const edited: Tasting = {
      ...kept,
      tastedOn: input.tastedOn,
      ...(input.rating !== undefined ? { rating: input.rating } : {}),
      ...(notes ? { notes } : {}),
    };
    return replaceWine(
      cellar,
      touched(wine, { tastings: wine.tastings.map((t) => (t.id === tasting.id ? edited : t)) }),
    );
  };
}

/** Deletes a wine with all its purchases and tastings (FR-029; the screen asks first). */
export function deleteWine(input: { wineId: string; expectedVersion: number }): CellarChange {
  return (cellar) => {
    wineAtVersion(cellar, input.wineId, input.expectedVersion);
    return { ...cellar, wines: cellar.wines.filter((w) => w.id !== input.wineId) };
  };
}

/** Deletes a purchase; refused if the stock would go below zero (FR-014). */
export function deletePurchase(input: {
  wineId: string;
  purchaseId: string;
  expectedVersion: number;
}): CellarChange {
  return (cellar) => {
    const wine = wineAtVersion(cellar, input.wineId, input.expectedVersion);
    if (!wine.purchases.some((p) => p.id === input.purchaseId)) throw new RecordChangedError();
    const updated = touched(wine, {
      purchases: wine.purchases.filter((p) => p.id !== input.purchaseId),
    });
    assertWineStockNotNegative(updated);
    return replaceWine(cellar, updated);
  };
}

/** Deletes a tasting; a bottle it took from the stock goes back into it. */
export function deleteTasting(input: {
  wineId: string;
  tastingId: string;
  expectedVersion: number;
}): CellarChange {
  return (cellar) => {
    const wine = wineAtVersion(cellar, input.wineId, input.expectedVersion);
    if (!wine.tastings.some((t) => t.id === input.tastingId)) throw new RecordChangedError();
    return replaceWine(
      cellar,
      touched(wine, { tastings: wine.tastings.filter((t) => t.id !== input.tastingId) }),
    );
  };
}
